import { useEffect, useState } from "react";
import { useParams, Link } from "wouter";
import { blogPosts } from "@/lib/blogData";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Calendar, Clock, ArrowLeft, ChevronRight, Eye } from "lucide-react";
import { trpc } from "@/lib/trpc";

function setMetaTag(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);
  const [viewCount, setViewCount] = useState<number | null>(null);

  // Track view on mount
  const trackView = trpc.blog.trackView.useMutation({
    onSuccess: (data) => setViewCount(data.viewCount),
  });

  useEffect(() => {
    if (slug) {
      trackView.mutate({ slug });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    const pageUrl = window.location.href;
    const ogImage = post.ogImage || "https://uniteddreammongolia.com/og-default.jpg";

    // Update document title
    document.title = `${post.title} | United Dream Mongolia`;

    // Open Graph tags
    setMetaTag("og:title", post.title);
    setMetaTag("og:description", post.excerpt);
    setMetaTag("og:image", ogImage);
    setMetaTag("og:image:width", "1200");
    setMetaTag("og:image:height", "630");
    setMetaTag("og:url", pageUrl);
    setMetaTag("og:type", "article");
    setMetaTag("og:site_name", "United Dream Mongolia");
    setMetaTag("og:locale", "mn_MN");

    // Twitter Card tags
    setMetaName("twitter:card", "summary_large_image");
    setMetaName("twitter:title", post.title);
    setMetaName("twitter:description", post.excerpt);
    setMetaName("twitter:image", ogImage);

    // Standard meta
    setMetaName("description", post.excerpt);

    return () => {
      // Restore default title on unmount
      document.title = "United Dream Mongolia";
    };
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F8F9FC]">
        <Header />
        <main className="pt-24 pb-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-black text-[#0A1628] mb-4">Нийтлэл олдсонгүй</h1>
            <Link href="/blog">
              <span className="text-[#1A3C6E] font-semibold hover:underline cursor-pointer">← Блог руу буцах</span>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleScrollToCostCalc = () => {
    // Navigate to home and scroll to cost calculator
    window.location.href = "/#cost-calculator";
  };

  return (
    <div className="min-h-screen bg-[#F8F9FC]">
      <Header />
      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-8">
            <Link href="/">
              <span className="hover:text-[#1A3C6E] cursor-pointer">Нүүр</span>
            </Link>
            <ChevronRight size={12} />
            <Link href="/blog">
              <span className="hover:text-[#1A3C6E] cursor-pointer">Блог</span>
            </Link>
            <ChevronRight size={12} />
            <span className="text-gray-600 truncate max-w-[200px]">{post.title}</span>
          </nav>

          {/* Article header */}
          <article className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 sm:p-10">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-2.5 py-0.5 rounded-full bg-[#1A3C6E]/10 text-[#1A3C6E] text-xs font-semibold">
                  {post.category}
                </span>
                <span className="flex items-center gap-1 text-gray-400 text-xs">
                  <Calendar size={12} />
                  {new Date(post.date).toLocaleDateString("mn-MN", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
                <span className="flex items-center gap-1 text-gray-400 text-xs">
                  <Clock size={12} />
                  {post.readTime}
                </span>
                {viewCount !== null && (
                  <span className="flex items-center gap-1 text-gray-400 text-xs">
                    <Eye size={12} />
                    {viewCount.toLocaleString()} үзэлт
                  </span>
                )}
              </div>

              <h1
                className="text-2xl sm:text-3xl font-black text-[#0A1628] mb-6 leading-tight"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {post.title}
              </h1>

              {/* Article content */}
              <div
                className="prose prose-sm sm:prose max-w-none text-gray-700 blog-content"
                style={{ fontFamily: "var(--font-body)" }}
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </div>

            {/* CTA Banner */}
            <div className="mx-6 sm:mx-10 mb-8 rounded-xl bg-gradient-to-r from-[#1A3C6E] to-[#2563EB] p-6 text-white">
              <h3 className="font-black text-lg mb-1" style={{ fontFamily: "var(--font-display)" }}>
                Таны визний зардал хэд вэ?
              </h3>
              <p className="text-white/80 text-sm mb-4" style={{ fontFamily: "var(--font-body)" }}>
                Улс болон визийн төрлөө сонгоод засгийн газрын хураамж, бүрдүүлэх материалыг шууд харна уу.
              </p>
              <button
                onClick={handleScrollToCostCalc}
                className="inline-flex items-center gap-2 bg-white text-[#1A3C6E] font-bold text-sm px-5 py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Бүрдүүлэх материал & зардал харах <ChevronRight size={14} />
              </button>
            </div>
          </article>

          {/* Back link */}
          <div className="mt-8">
            <Link href="/blog">
              <span className="inline-flex items-center gap-1.5 text-[#1A3C6E] text-sm font-semibold hover:gap-2.5 transition-all cursor-pointer">
                <ArrowLeft size={14} /> Блог руу буцах
              </span>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
